// ✅ BackendTaskPanel.js
import React, { useState, useEffect } from "react";
import { apiRequest, apiRequestFile } from "../utils/api";
import { useAuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "../styles/BackendTaskPanel.css";

const BackendTaskPanel = () => {
  const [progress, setProgress] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [imageCheckProgress, setImageCheckProgress] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [showUploadConfirm, setShowUploadConfirm] = useState(false);

  const [scrapeUrl, setScrapeUrl] = useState("");
  const [surugayaProgress, setSurugayaProgress] = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [activeSessions, setActiveSessions] = useState([]);
  const { user: userInfo } = useAuthContext();
  const [summary, setSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const [qrUrl, setQrUrl] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  // ▼ 免税CSV取込
  const [taxExemptCsvFile, setTaxExemptCsvFile] = useState(null);
  const [taxExemptImporting, setTaxExemptImporting] = useState(false);
  const [taxExemptImportResult, setTaxExemptImportResult] = useState(null);
  const [taxExemptImportError, setTaxExemptImportError] = useState("");

  // ▼ 免税方式設定
  const [taxFreeMode, setTaxFreeMode] = useState("legacy");
  const [taxFreeModeLoading, setTaxFreeModeLoading] = useState(true);
  const [taxFreeModeSaving, setTaxFreeModeSaving] = useState(false);

  // ▼ 2FA再設定
  const [twoFactorResetOpen, setTwoFactorResetOpen] = useState(false);
  const [twoFactorQrUrl, setTwoFactorQrUrl] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorResetStep, setTwoFactorResetStep] = useState("confirm");
  const [twoFactorResetError, setTwoFactorResetError] = useState("");
  const [twoFactorResetLoading, setTwoFactorResetLoading] = useState(false);
  
  // ▼ 2FA強制解除
  const [twoFactorUsers, setTwoFactorUsers] = useState([]);
  const [selectedTwoFactorUserId, setSelectedTwoFactorUserId] = useState("");
  
  const openQrModal = (sessionId) => {
    const url = `${window.location.origin}/stock_in_M/${sessionId}`;
    const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      url
    )}`;
    setQrUrl(qr);
    setQrOpen(true);
  };

  const fetchCategories = async () => {
    const res = await apiRequest("inventory/categorys/");
    setCategories(res);
  };

  useEffect(() => {
    fetchActiveSessions();
    fetchCategories();
    fetchTwoFactorUsers();
    fetchTaxFreeMode();
  }, []);

  const fetchTaxFreeMode = async () => {
    try {
      setTaxFreeModeLoading(true);

      const res = await apiRequest(
        "inventory/system-setting/",
        "GET"
      );

      setTaxFreeMode(res.tax_free_mode || "legacy");
    } catch (err) {
      console.error("免税方式取得失敗:", err);
    } finally {
      setTaxFreeModeLoading(false);
    }
  };

  const handleTempImageUpload = async () => {
    setUploadInProgress(true);
    setUploadError(false);
    setShowUploadConfirm(false);

    try {
      const res = await apiRequest("inventory/upload_from_temp_images/", "POST");
      const taskId = res.task_id;

      const interval = setInterval(async () => {
        try {
          const progressRes = await apiRequest(
            `inventory/scrape_progress/?task_id=${taskId}`,
            "GET"
          );
          const prog = progressRes.progress;
          setUploadProgress(prog);

          if (prog >= 100) {
            clearInterval(interval);
            setUploadInProgress(false);
            setShowUploadConfirm(true);
          }
        } catch (e) {
          clearInterval(interval);
          setUploadInProgress(false);
          setUploadError(true);
        }
      }, 1000);
    } catch (err) {
      console.error("アップロード処理失敗:", err);
      setUploadInProgress(false);
      setUploadError(true);
    }
  };

  const handleTaxExemptCsvImport = async () => {
    if (!taxExemptCsvFile) {
      alert("免税CSVファイルを選択してください");
      return;
    }

    const ok = window.confirm(
      "選択した免税CSVを取り込みます。\nレシート番号で販売履歴と紐づけます。"
    );

    if (!ok) return;

    setTaxExemptImporting(true);
    setTaxExemptImportError("");
    setTaxExemptImportResult(null);

    try {
      const res = await apiRequestFile(
        "inventory/tax_exempt/import_csv/",
        "POST",
        taxExemptCsvFile
      );

      setTaxExemptImportResult(res);
      alert("免税CSVを取り込みました");
    } catch (err) {
      console.error("免税CSV取込失敗:", err);
      setTaxExemptImportError(
        err.details?.error ||
          err.details?.message ||
          "免税CSVの取り込みに失敗しました"
      );
    } finally {
      setTaxExemptImporting(false);
    }
  };

  const handleTaxFreeModeChange = async (checked) => {
    const nextMode = checked ? "refund" : "legacy";

    const message = checked
      ? "免税方式を「リファンド方式」に変更しますか？\n免税販売でも消費税を徴収する方式になります。"
      : "免税方式を「従来免税」に戻しますか？";

    if (!window.confirm(message)) {
      return;
    }

    setTaxFreeModeSaving(true);

    try {
      const res = await apiRequest(
        "inventory/system-setting/",
        "PATCH",
        {
          tax_free_mode: nextMode,
        }
      );

      setTaxFreeMode(res.tax_free_mode);

      alert(
        res.tax_free_mode === "refund"
          ? "リファンド方式に変更しました"
          : "従来免税方式に変更しました"
      );
    } catch (err) {
      console.error("免税方式変更失敗:", err);
      alert("免税方式の変更に失敗しました");
    } finally {
      setTaxFreeModeSaving(false);
    }
  };

  // ▼ 免税案内PDFエディターを開く
  const handleOpenJPrefundEditor = () => {
    if (!window.electronAPI?.openJPrefundEditor) {
      alert("この機能はElectronアプリから利用してください。");
      return;
    }

    window.electronAPI.openJPrefundEditor();
  };

  const handleUploadReset = () => {
    setUploadInProgress(false);
    setUploadError(false);
    setShowUploadConfirm(false);
  };

  const startImageCheck = async () => {
    setImageCheckProgress(0);
    setIsError(false);
    setIsDone(false);
    setShowConfirm(false);

    try {
      const data = await apiRequest("inventory/image_check/start/", "POST");
      const id = data.job_id;
      setJobId(id);

      const interval = setInterval(async () => {
        try {
          const res = await apiRequest(
            `inventory/image_check/progress/${id}/`,
            "GET"
          );
          const current = res.progress;
          setImageCheckProgress(current);

          if (current === -1) {
            clearInterval(interval);
            setIsError(true);
          }

          if (current >= 100) {
            clearInterval(interval);
          }
        } catch (err) {
          clearInterval(interval);
          setIsError(true);
        }
      }, 1000);
    } catch (err) {
      console.error("開始失敗:", err);
      setIsError(true);
    }
  };

  useEffect(() => {
    if (progress === 100 && !isDone) {
      setShowConfirm(true);
    }
  }, [progress, isDone]);

  const handleConfirmReset = async () => {
    try {
      await apiRequest(`inventory/image_check/clear/${jobId}/`, "POST");
    } catch (err) {
      console.error("リセット失敗:", err);
    }

    setProgress(null);
    setIsDone(true);
    setShowConfirm(false);
  };

  const startSurugayaScrape = async () => {
    if (!scrapeUrl) return;

    setSurugayaProgress(0);

    try {
      const res = await apiRequest("inventory/surugaya_scrape/start/", "POST", {
        baseurl: scrapeUrl,
      });
      const id = res.job_id;

      const interval = setInterval(async () => {
        try {
          const res = await apiRequest(
            `inventory/surugaya_scrape/progress/${id}/`,
            "GET"
          );
          const current = res.progress;
          setSurugayaProgress(current);

          if (current >= 100) {
            clearInterval(interval);
          }
        } catch (err) {
          console.error("進捗取得エラー", err);
          clearInterval(interval);
          setSurugayaProgress(-1);
        }
      }, 1000);
    } catch (err) {
      console.error("スクレイピング開始失敗", err);
      setSurugayaProgress(-1);
    }
  };

  // 棚卸
  const fetchActiveSessions = async () => {
    const res = await apiRequest("inventory/sessions/active/");
    setActiveSessions(res);
  };

  const handleStart = async () => {
    if (!userInfo?.store_id) {
      alert("store_id がまだ取得できていません");
      return;
    }

    if (!selectedCategory) {
      alert("カテゴリを選択してください");
      return;
    }

    await apiRequest("inventory/sessions/start/", "POST", {
      category_id: selectedCategory,
      store_id: userInfo.store_id,
    });

    fetchActiveSessions();
  };

  const handleComplete = async (sessionId) => {
    try {
      const res = await apiRequest(
        `inventory/sessions/${sessionId}/complete/`,
        "POST"
      );

      console.log("complete成功:", res);
      fetchActiveSessions();
    } catch (error) {
      console.error("complete失敗 status:", error.status);
      console.error("complete失敗 details:", error.details);
      alert(
        error.details?.error ||
          error.details?.message ||
          JSON.stringify(error.details) ||
          "完了処理に失敗しました"
      );
    }
  };

  // 棚卸現状
  const fetchSummary = async () => {
    try {
      const res = await apiRequest(
        `inventory/summary-by-category/?store_id=${userInfo.store_id}`
      );

      setSummary(res.categories || []);
      setGrandTotal(res.grand_total_value || 0);
      setShowModal(true);
    } catch (err) {
      console.error(err);
    }
  };

  const closeModal = (event) => {
    if (event.target === event.currentTarget) {
      setShowModal(false);
    }
  };

  // ▼ 2FA再設定
  const openTwoFactorResetModal = () => {
    setTwoFactorResetOpen(true);
    setTwoFactorResetStep("confirm");
    setTwoFactorQrUrl("");
    setTwoFactorCode("");
    setTwoFactorResetError("");
    setTwoFactorResetLoading(false);
  };

  const closeTwoFactorResetModal = () => {
    setTwoFactorResetOpen(false);
    setTwoFactorResetStep("confirm");
    setTwoFactorQrUrl("");
    setTwoFactorCode("");
    setTwoFactorResetError("");
    setTwoFactorResetLoading(false);
  };

  const handleStartTwoFactorReset = async () => {
    setTwoFactorResetLoading(true);
    setTwoFactorResetError("");

    try {
      const res = await apiRequest("accounts/2fa/reset/", "POST");

      setTwoFactorQrUrl(res.qr_url || res.qrUrl || "");
      setTwoFactorResetStep("qr");
    } catch (err) {
      console.error("2FA再設定開始失敗:", err);
      setTwoFactorResetError(
        err.details?.error ||
          err.details?.message ||
          "2段階認証の再設定開始に失敗しました"
      );
    } finally {
      setTwoFactorResetLoading(false);
    }
  };

  const handleConfirmTwoFactorReset = async () => {
    if (twoFactorCode.length !== 6) return;

    setTwoFactorResetLoading(true);
    setTwoFactorResetError("");

    try {
      await apiRequest("accounts/2fa/confirm-reset/", "POST", {
        code: twoFactorCode,
      });

      alert("2段階認証を再設定しました");
      closeTwoFactorResetModal();
    } catch (err) {
      console.error("2FA再設定確認失敗:", err);
      setTwoFactorResetError(
        err.details?.error ||
          err.details?.message ||
          "認証コードが正しくありません"
      );
    } finally {
      setTwoFactorResetLoading(false);
    }
  };
  
  // ▼ 2FA強制解除
  const fetchTwoFactorUsers = async () => {
    try {
      const res = await apiRequest("accounts/users/", "GET");
      setTwoFactorUsers(res);
    } catch (err) {
      console.error("従業員一覧取得失敗:", err);
      alert("従業員一覧の取得に失敗しました");
    }
  };

  const handleAdminResetTwoFactor = async () => {
    if (!selectedTwoFactorUserId) {
      alert("従業員を選択してください");
      return;
    }

    const ok = window.confirm(
      "選択した従業員の2段階認証を強制リセットします。\n次回ログイン時に再設定が必要になります。"
    );

    if (!ok) return;

    try {
      await apiRequest("accounts/2fa/admin-reset/", "POST", {
        user_id: selectedTwoFactorUserId,
      });

      alert("2段階認証を強制リセットしました");
      setSelectedTwoFactorUserId("");
    } catch (err) {
      console.error("2FA強制リセット失敗:", err);
      alert(err.details?.error || "2FA強制リセットに失敗しました");
    }
  };

  return (
    <>
      <div className="backend-task-panel">
        {/* ▼ 一時画像アップロードパネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">📤 一時画像アップロード</h3>

          <button
            className="backend-task-upload-button"
            onClick={handleTempImageUpload}
          >
            アップロードする
          </button>

          {uploadInProgress && (
            <div className="backend-task-progress-box">
              <span className="backend-task-progress-text">
                進捗: {uploadProgress}%
              </span>
            </div>
          )}

          {uploadError && (
            <div className="backend-task-progress-box">
              <span className="backend-task-error-text">
                ❌ アップロードに失敗しました
              </span>
            </div>
          )}

          {showUploadConfirm && (
            <div className="backend-task-confirm-box">
              ✅ アップロードが完了しました。
              <br />
              <button
                className="backend-task-ok-button"
                onClick={handleUploadReset}
              >
                OK
              </button>
            </div>
          )}
        </div>

        {/* ▼ 免税方式設定 */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">🧾 免税方式設定</h3>

          <p
            style={{
              margin: "0 0 12px",
              fontSize: "14px",
              color: "#555",
            }}
          >
            現在の免税販売方式を設定します。
          </p>

          {taxFreeModeLoading ? (
            <div>読込中...</div>
          ) : (
            <>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: taxFreeModeSaving ? "default" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={taxFreeMode === "refund"}
                  onChange={(e) =>
                    handleTaxFreeModeChange(e.target.checked)
                  }
                  disabled={taxFreeModeSaving}
                  style={{
                    width: "22px",
                    height: "22px",
                  }}
                />

                <span style={{ fontWeight: "bold" }}>
                  リファンド方式
                </span>
              </label>

              <div
                style={{
                  marginTop: "10px",
                  fontWeight: "bold",
                  color:
                    taxFreeMode === "refund"
                      ? "#d97706"
                      : "#555",
                }}
              >
                現在：
                {taxFreeMode === "refund"
                  ? "リファンド方式"
                  : "従来免税方式"}
              </div>

                            {taxFreeMode === "refund" && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 10px",
                    background: "#fff3cd",
                    color: "#664d03",
                    borderRadius: "6px",
                  }}
                >
                  免税販売でも消費税を徴収する設定です。
                </div>
              )}
            </>
          )}

          {/* ▼ 免税案内PDF編集 */}
          <div
            style={{
              marginTop: "15px",
              paddingTop: "12px",
              borderTop: "1px solid #ccc",
            }}
          >
            <button
              className="backend-task-start-button"
              onClick={handleOpenJPrefundEditor}
            >
              🖨 免税案内PDF編集
            </button>

            <p
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#666",
              }}
            >
              JPrefund・免税案内QRのレイアウト編集とPDF作成
            </p>
          </div>

        </div>
        
        {/* ▼ 免税CSV取込パネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">🧾 免税CSV取込</h3>

          <p style={{ margin: "0 0 10px", fontSize: "14px", color: "#555" }}>
            免税販売一覧CSVを取り込み、レシート番号で販売履歴と紐づけます。
          </p>

          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              setTaxExemptCsvFile(e.target.files?.[0] || null);
              setTaxExemptImportResult(null);
              setTaxExemptImportError("");
            }}
            style={{ margin: "0 10px 10px 0" }}
          />

          <button
            className="backend-task-start-button"
            onClick={handleTaxExemptCsvImport}
            disabled={!taxExemptCsvFile || taxExemptImporting}
          >
            {taxExemptImporting ? "取込中..." : "CSVを取り込む"}
          </button>

          {taxExemptImportError && (
            <div className="backend-task-progress-box">
              <span className="backend-task-error-text">
                ❌ {taxExemptImportError}
              </span>
            </div>
          )}

          {taxExemptImportResult && (
            <div className="backend-task-confirm-box">
              ✅ {taxExemptImportResult.message || "取込完了"}
              <br />
              取込行数: {taxExemptImportResult.imported_rows ?? 0}
              <br />
              新規: {taxExemptImportResult.created_rows ?? 0} / 更新:{" "}
              {taxExemptImportResult.updated_rows ?? 0}
              <br />
              紐づけ成功: {taxExemptImportResult.linked_receipts ?? 0}
              <br />
              未紐づけ: {taxExemptImportResult.unlinked_receipts ?? 0}
              <br />
              複数レシート行: {taxExemptImportResult.multi_receipt_rows ?? 0}
              {taxExemptImportResult.unlinked_details?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <strong>未紐づけ詳細</strong>

                <table style={{ width: "100%", marginTop: 6, borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #777", padding: 4 }}>CSV行</th>
                      <th style={{ border: "1px solid #777", padding: 4 }}>譲渡日</th>
                      <th style={{ border: "1px solid #777", padding: 4 }}>国籍</th>
                      <th style={{ border: "1px solid #777", padding: 4 }}>元レシート欄</th>
                      <th style={{ border: "1px solid #777", padding: 4 }}>未紐づけ番号</th>
                    </tr>
                  </thead>

                  <tbody>
                    {taxExemptImportResult.unlinked_details.map((row, index) => (
                      <tr key={`${row.receipt_number}-${index}`}>
                        <td style={{ border: "1px solid #777", padding: 4 }}>
                          {row.row_number}
                        </td>
                        <td style={{ border: "1px solid #777", padding: 4 }}>
                          {row.transfer_date}
                        </td>
                        <td style={{ border: "1px solid #777", padding: 4 }}>
                          {row.nationality}
                        </td>
                        <td style={{ border: "1px solid #777", padding: 4 }}>
                          {row.receipt_number_raw}
                        </td>
                        <td style={{ border: "1px solid #777", padding: 4, fontWeight: "bold" }}>
                          {row.receipt_number}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          )}
        </div>

        {/* ▼ 駿河屋スクレイピングパネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">🕷 駿河屋一括スクレイピング</h3>

          <input
            type="text"
            value={scrapeUrl}
            onChange={(e) => setScrapeUrl(e.target.value)}
            placeholder="https://www.suruga-ya.jp/search_buy?..."
            className="backend-task-url-input"
          />

          <button
            className="backend-task-start-button"
            onClick={startSurugayaScrape}
            disabled={!scrapeUrl}
          >
            実行する
          </button>

          {surugayaProgress !== null && (
            <div className="backend-task-progress-box">
              {surugayaProgress < 0 ? (
                <span className="backend-task-error-text">
                  ❌ エラーが発生しました
                </span>
              ) : surugayaProgress < 100 ? (
                <span className="backend-task-progress-text">
                  進捗: {surugayaProgress}%
                </span>
              ) : (
                <span className="backend-task-done-text">✅ 完了しました</span>
              )}
            </div>
          )}
        </div>

        {/* ▼ 画像チェックパネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">📦 画像チェック</h3>

          <button
            className="backend-task-start-button"
            onClick={startImageCheck}
          >
            実行する
          </button>

          {imageCheckProgress !== null && (
            <div className="backend-task-progress-box">
              {isError ? (
                <span className="backend-task-error-text">
                  ❌ エラーが発生しました
                </span>
              ) : imageCheckProgress < 100 ? (
                <span className="backend-task-progress-text">
                  進捗: {imageCheckProgress}%
                </span>
              ) : (
                <span className="backend-task-done-text">✅ 完了しました</span>
              )}
            </div>
          )}

          {showConfirm && (
            <div className="backend-task-confirm-box">
              ✅ 完了しました。
              <br />
              <button
                className="backend-task-ok-button"
                onClick={handleConfirmReset}
              >
                OK
              </button>
            </div>
          )}
        </div>

        {/* ▼ 棚卸パネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">棚卸管理</h3>

          <div>
            <button className="backend-task-start-button" onClick={fetchSummary}>
              現状確認
            </button>
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ margin: "0 10px 0 0" }}
          >
            <option value="">カテゴリー選択</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.category_name}
              </option>
            ))}
          </select>

          <button className="backend-task-start-button" onClick={handleStart}>
            開始
          </button>

          <h3>実施中カテゴリー</h3>

          {activeSessions.map((session) => (
            <div key={session.id}>
              <span
                style={{ cursor: "pointer", margin: "0 10px 0 0" }}
                onClick={() => navigate(`/stock_in_M/${session.id}`)}
              >
                {session.category}
              </span>

              <button
                className="backend-task-start-button"
                onClick={() => openQrModal(session.id)}
                style={{ margin: "0 5px 0 0" }}
              >
                QR
              </button>

              <button
                className="backend-task-start-button"
                onClick={() => navigate(`/inventory_diff/${session.id}`)}
                style={{ margin: "0 5px 0 0" }}
              >
                確認
              </button>

              <button
                className="backend-task-start-button"
                onClick={() => handleComplete(session.id)}
                style={{ margin: "0 5px 0 0" }}
              >
                終了
              </button>
            </div>
          ))}
        </div>

        {/* ▼ シフトマスターパネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">シフトマスター</h3>

          <button
            className="backend-task-start-button"
            onClick={() => navigate(`/shift_staff_master`)}
          >
            従業員リスト
          </button>

          <button
            className="backend-task-start-button"
            onClick={() => navigate(`/shift_location_master`)}
          >
            勤務場所リスト
          </button>
        </div>

        {/* ▼ 2段階認証パネル */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">🔐 2段階認証</h3>

          <p style={{ margin: "0 0 10px", fontSize: "14px", color: "#555" }}>
            認証アプリを変更・紛失した場合に、2段階認証を再設定します。
          </p>

          <button
            className="backend-task-start-button"
            onClick={openTwoFactorResetModal}
          >
            2段階認証を再設定
          </button>
        </div>
        {/* ▼ 管理者用 2FA強制リセット */}
        <div className="backend-task-section">
          <h3 className="backend-task-title">🔐 従業員2FA強制リセット</h3>

          <p style={{ margin: "0 0 10px", fontSize: "14px", color: "#555" }}>
            認証アプリ紛失時などに、対象従業員の2段階認証を強制解除します。
          </p>

          <select
            value={selectedTwoFactorUserId}
            onChange={(e) => setSelectedTwoFactorUserId(e.target.value)}
            style={{ margin: "0 10px 0 0" }}
          >
            <option value="">従業員を選択</option>
            {twoFactorUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username} / {user.email || "メールなし"}
              </option>
            ))}
          </select>

          <button
            className="backend-task-start-button"
            onClick={handleAdminResetTwoFactor}
            disabled={!selectedTwoFactorUserId}
          >
            2FAを強制リセット
          </button>
        </div>
      </div>

      {/* ▼ 棚卸 現状確認モーダル */}
      {showModal && (
        <div
          className={`modal-overlay ${showModal ? "open" : ""}`}
          onClick={closeModal}
        >
          <div className="modal-box">
            <span className="close" onClick={() => setShowModal(false)}>
              &times;
            </span>

            <h2>カテゴリー別 現状在庫</h2>

            <table>
              <thead>
                <tr>
                  <th>カテゴリー</th>
                  <th>在庫合計</th>
                  <th>売値総額</th>
                </tr>
              </thead>

              <tbody>
                {summary.map((row) => (
                  <tr key={row.id}>
                    <td>{row.category_name}</td>
                    <td>{row.total_stock}</td>
                    <td>{Number(row.total_value).toLocaleString()} 円</td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr style={{ fontWeight: "bold" }}>
                  <td>総額</td>
                  <td></td>
                  <td>{Number(grandTotal).toLocaleString()} 円</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ▼ 棚卸QRモーダル */}
      {qrOpen && (
        <div
          onClick={() => setQrOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <img src={qrUrl} alt="棚卸QR" width="220" height="220" />
            <br />

            <button onClick={() => setQrOpen(false)} style={{ marginTop: 12 }}>
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* ▼ 2FA再設定モーダル */}
      {twoFactorResetOpen && (
        <div
          className="modal-overlay open"
          onClick={closeTwoFactorResetModal}
        >
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={closeTwoFactorResetModal}>
              &times;
            </span>

            <h2>2段階認証の再設定</h2>

            {twoFactorResetError && (
              <div
                style={{
                  margin: "10px 0",
                  padding: "10px",
                  background: "#ffecec",
                  color: "#c00",
                  borderRadius: "6px",
                  fontWeight: "bold",
                }}
              >
                {twoFactorResetError}
              </div>
            )}

            {twoFactorResetStep === "confirm" && (
              <>
                <p style={{ lineHeight: 1.7 }}>
                  現在の2段階認証設定をリセットして、新しいQRコードを発行します。
                  <br />
                  再設定が完了すると、古い認証アプリのコードは使えなくなります。
                </p>

                <button
                  className="backend-task-start-button"
                  onClick={handleStartTwoFactorReset}
                  disabled={twoFactorResetLoading}
                >
                  {twoFactorResetLoading ? "処理中..." : "再設定を開始する"}
                </button>

                <button
                  className="backend-task-start-button"
                  onClick={closeTwoFactorResetModal}
                  style={{ marginLeft: 8 }}
                  disabled={twoFactorResetLoading}
                >
                  キャンセル
                </button>
              </>
            )}

            {twoFactorResetStep === "qr" && (
              <>
                <p style={{ lineHeight: 1.7 }}>
                  認証アプリでQRコードを読み取ってください。
                  <br />
                  読み取り後、表示された6桁の認証コードを入力してください。
                </p>

                {twoFactorQrUrl ? (
                  <div style={{ textAlign: "center", margin: "15px 0" }}>
                    <img
                      src={twoFactorQrUrl}
                      alt="2FA QR"
                      style={{
                        width: 220,
                        height: 220,
                        border: "1px solid #ddd",
                        padding: 8,
                        background: "#fff",
                      }}
                    />
                  </div>
                ) : (
                  <p style={{ color: "#c00" }}>
                    QRコードURLが取得できませんでした。
                  </p>
                )}

                <input
                  type="text"
                  inputMode="numeric"
                  value={twoFactorCode}
                  onChange={(e) =>
                    setTwoFactorCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="6桁の認証コード"
                  maxLength={6}
                  className="backend-task-url-input"
                  style={{
                    marginTop: 12,
                    textAlign: "center",
                    fontSize: 20,
                    letterSpacing: 4,
                  }}
                />

                <div style={{ marginTop: 15 }}>
                  <button
                    className="backend-task-start-button"
                    onClick={handleConfirmTwoFactorReset}
                    disabled={twoFactorCode.length !== 6 || twoFactorResetLoading}
                  >
                    {twoFactorResetLoading ? "確認中..." : "確定する"}
                  </button>

                  <button
                    className="backend-task-start-button"
                    onClick={closeTwoFactorResetModal}
                    style={{ marginLeft: 8 }}
                    disabled={twoFactorResetLoading}
                  >
                    閉じる
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BackendTaskPanel;